import React, { useEffect, useState } from 'react'
import { compressImage } from '../utils/imageCompression'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import toast from 'react-hot-toast'
import { 
  Key, 
  Copy, 
  CheckCircle, 
  XCircle,
  Clock,
  Plus,
  RefreshCw,
  Camera
} from 'lucide-react'

interface AdminProfile {
  first_name: string
  last_name: string
  department: string
  profile_picture: string | null
  admin_scope: string
  allowed_departments: string[] | null
}

interface AdminCode {
  id: string
  code: string
  created_by: string
  used_by: string | null
  is_active: boolean
  expires_at: string
  created_at: string
  used_at: string | null
  admin_type: string | null
  for_department: string | null
}

const AdminProfilePage: React.FC = () => {
  const { user } = useAuthStore()
  const [profile, setProfile] = useState<AdminProfile>({
    first_name: '',
    last_name: '',
    department: '',
    profile_picture: null,
    admin_scope: 'department',
    allowed_departments: null
  })
  const [originalProfile, setOriginalProfile] = useState<AdminProfile>({
    first_name: '',
    last_name: '',
    department: '',
    profile_picture: null,
    admin_scope: 'department',
    allowed_departments: null
  })
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [adminCodes, setAdminCodes] = useState<AdminCode[]>([])
  const [generatingCode, setGeneratingCode] = useState(false)
  const [codeType, setCodeType] = useState<'department' | 'super_admin'>('department')
  const [codeDepartment, setCodeDepartment] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string>('')
  const [codesGeneratedToday, setCodesGeneratedToday] = useState(0)
  const [departments, setDepartments] = useState<Array<{ id: string; name: string }>>([])
  const [_isLoadingDepartments, setIsLoadingDepartments] = useState(true)

  useEffect(() => {
    fetchProfile()
    fetchAdminCodes()
  }, [user])

  useEffect(() => {
    fetchDepartments()
  }, [])

  const fetchProfile = async () => {
    if (!user) return

    try {
      setLoading(true)
      const { data, error } = await supabaseClient
        .from('admin_profiles')
        .select('first_name, last_name, department, profile_picture, admin_scope, allowed_departments')
        .eq('user_id', user.id)
        .single()

      if (error) throw error

      if (data) {
        setProfile(data)
        setOriginalProfile(data)
        setImagePreview(data.profile_picture || '')
      }
    } catch (error) {
      console.error('Error fetching profile:', error)
      toast.error('Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  const fetchAdminCodes = async () => {
    if (!user) return

    try {
      // First, delete old expired codes (past 24 hours from expiration)
      await cleanupExpiredCodes()
      
      const { data, error } = await supabaseClient
        .from('admin_codes')
        .select('*')
        .eq('created_by', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error

      setAdminCodes(data || [])
      
      // Count codes generated today
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const todayCount = data?.filter(code => 
        new Date(code.created_at) >= today
      ).length || 0
      setCodesGeneratedToday(todayCount)
    } catch (error) {
      console.error('Error fetching admin codes:', error)
    }
  }

  const fetchDepartments = async () => {
    try {
      setIsLoadingDepartments(true)
      const { data, error } = await supabaseClient
        .from('departments')
        .select('id, name, is_active, display_order')
        .eq('is_active', true)
        .order('display_order', { ascending: true })

      if (error) throw error

      setDepartments((data || []).map(({ id, name }) => ({ id, name })))
    } catch (error) {
      console.error('Error loading departments:', error)
      toast.error('Failed to load colleges list')
    } finally {
      setIsLoadingDepartments(false)
    }
  }

  const cleanupExpiredCodes = async () => {
    if (!user) return

    try {
      const now = new Date()
      
      // Delete codes that expired more than 24 hours ago
      const { error } = await supabaseClient
        .from('admin_codes')
        .delete()
        .eq('created_by', user.id)
        .lt('expires_at', now.toISOString())

      if (error) {
        console.error('Error cleaning up expired codes:', error)
      }
    } catch (error) {
      console.error('Error in cleanup:', error)
    }
  }

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const uploadProfilePicture = async (): Promise<string | null> => {
    if (!selectedFile || !user) return null

    try {
      const compressed = await compressImage(selectedFile, { maxWidth: 400, maxHeight: 400, quality: 0.85 })
      const fileName = `${user.id}-${Date.now()}.jpg`
      const filePath = `admin-profiles/${fileName}`

      const { error: uploadError } = await supabaseClient.storage
        .from('admin-profiles')
        .upload(filePath, compressed, {
          upsert: true,
          contentType: 'image/jpeg'
        })

      if (uploadError) {
        console.error('Upload error:', uploadError)
        if (uploadError.message.includes('not found') || uploadError.message.includes('bucket')) {
          toast.error('Storage bucket not set up. Please create "admin-profiles" bucket in Supabase Storage.')
        } else if (uploadError.message.includes('policy')) {
          toast.error('Permission denied. Please check storage policies in Supabase.')
        } else {
          toast.error(`Upload failed: ${uploadError.message}`)
        }
        throw uploadError
      }

      const { data } = supabaseClient.storage
        .from('admin-profiles')
        .getPublicUrl(filePath)

      return data.publicUrl
    } catch (error: any) {
      console.error('Error uploading image:', error)
      return null
    }
  }

  const handleSaveProfile = async () => {
    if (!user) return

    try {
      setSaving(true)

      // Upload profile picture if a new one was selected
      let profilePictureUrl = profile.profile_picture
      if (selectedFile) {
        const uploadedUrl = await uploadProfilePicture()
        if (uploadedUrl) {
          profilePictureUrl = uploadedUrl
        }
      }

      const { error } = await supabaseClient
        .from('admin_profiles')
        .update({
          first_name: profile.first_name,
          last_name: profile.last_name,
          department: profile.department,
          profile_picture: profilePictureUrl,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)

      if (error) throw error

      // Also update full_name in users table
      const { error: userError } = await supabaseClient
        .from('users')
        .update({
          full_name: `${profile.first_name} ${profile.last_name}`,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)

      if (userError) throw userError

      const updatedProfile = { ...profile, profile_picture: profilePictureUrl }
      setProfile(updatedProfile)
      setOriginalProfile(updatedProfile)
      setIsEditing(false)
      setSelectedFile(null)
      toast.success('Profile updated successfully')
    } catch (error) {
      console.error('Error updating profile:', error)
      toast.error('Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const generateAdminCode = async () => {
    if (!user) return

    // For super admins, validate that a department/office is selected
    // For department admins, use their own department automatically
    const isDepartmentAdmin = profile.admin_scope === 'department'
    const targetDepartment = isDepartmentAdmin ? profile.department : codeDepartment
    
    if (!targetDepartment) {
      toast.error('Please select a department for the admin code')
      return
    }

    try {
      setGeneratingCode(true)
      
      // Check how many codes were generated today
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      
      const { data: todayCodes, error: countError } = await supabaseClient
        .from('admin_codes')
        .select('id', { count: 'exact' })
        .eq('created_by', user.id)
        .gte('created_at', today.toISOString())
      
      if (countError) throw countError
      
      const codesGeneratedToday = todayCodes?.length || 0
      
      if (codesGeneratedToday >= 3) {
        toast.error('Daily limit reached! You can only generate 3 codes per day. Try again tomorrow.')
        setGeneratingCode(false)
        return
      }
      
      // Generate a random 8-character code
      const code = Math.random().toString(36).substring(2, 10).toUpperCase()
      
      // Fixed 24-hour expiration
      const expiresAt = new Date()
      expiresAt.setHours(expiresAt.getHours() + 24)

      const { data, error } = await supabaseClient
        .from('admin_codes')
        .insert({
          code: code,
          created_by: user.id,
          is_active: true,
          expires_at: expiresAt.toISOString(),
          used_at: null,
          used_by: null,
          admin_type: isDepartmentAdmin ? 'department' : codeType,
          for_department: targetDepartment
        })
        .select()
        .single()

      if (error) throw error

      setAdminCodes(prev => [data, ...prev])
      setCodesGeneratedToday(prev => prev + 1)
      const remaining = 3 - codesGeneratedToday - 1
      toast.success(`Admin code generated successfully! ${remaining} code${remaining !== 1 ? 's' : ''} remaining today.`)
    } catch (error) {
      console.error('Error generating code:', error)
      toast.error('Failed to generate admin code')
    } finally {
      setGeneratingCode(false)
    }
  }

  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code)
    toast.success('Code copied to clipboard!')
  }

  const deactivateCode = async (codeId: string) => {
    try {
      const { error } = await supabaseClient
        .from('admin_codes')
        .update({ is_active: false })
        .eq('id', codeId)

      if (error) throw error

      setAdminCodes(prev => prev.map(c => 
        c.id === codeId ? { ...c, is_active: false } : c
      ))
      toast.success('Code deactivated')
    } catch (error) {
      console.error('Error deactivating code:', error)
      toast.error('Failed to deactivate code')
    }
  }

  const isCodeExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
        <p className="mt-2 text-gray-600">Manage your profile information and admin settings</p>
      </div>

      {/* Profile Information Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-8">
          {/* Profile Picture and Edit Button */}
          <div className="flex items-start justify-between mb-8">
            <div className="flex items-center gap-6">
              <div className="relative">
                <img
                  src={imagePreview || '/default-avatar.svg'}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-4 border-gray-100"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/default-avatar.svg'
                  }}
                />
                {isEditing && (
                  <label className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 transition-colors shadow-lg">
                    <Camera className="w-4 h-4" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {profile.first_name} {profile.last_name}
                </h2>
                <p className="text-gray-600">{user?.email}</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-sm text-gray-500">{profile.department}</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    profile.admin_scope === 'super_admin' 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {profile.admin_scope === 'super_admin' ? 'Super Admin' : 'Department Admin'}
                  </span>
                </div>
              </div>
            </div>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Edit
              </button>
            )}
          </div>

          {/* Form Fields */}
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={profile.first_name}
                  onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                  disabled={!isEditing}
                  className={`w-full px-4 py-3 border rounded-lg transition-colors ${
                    isEditing 
                      ? 'border-gray-300 bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent' 
                      : 'border-gray-200 bg-gray-50 text-gray-600 cursor-not-allowed'
                  }`}
                  placeholder="Your First Name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={profile.last_name}
                  onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                  disabled={!isEditing}
                  className={`w-full px-4 py-3 border rounded-lg transition-colors ${
                    isEditing 
                      ? 'border-gray-300 bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent' 
                      : 'border-gray-200 bg-gray-50 text-gray-600 cursor-not-allowed'
                  }`}
                  placeholder="Your Last Name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                College
              </label>
              <input
                type="text"
                value={profile.department}
                disabled={true}
                className="w-full px-4 py-3 border border-gray-200 bg-gray-50 text-gray-600 cursor-not-allowed rounded-lg"
              />
              <p className="mt-1 text-xs text-gray-500">Department cannot be changed after registration</p>
            </div>

            {isEditing && (
              <div className="flex gap-3 pt-6 border-t border-gray-200">
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className={`flex-1 py-3 rounded-lg font-semibold transition-colors ${
                    saving
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setProfile(originalProfile)
                    setImagePreview(originalProfile.profile_picture || '')
                    setSelectedFile(null)
                    setIsEditing(false)
                  }}
                  className="px-8 py-3 rounded-lg font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Admin Code Generation Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 text-gray-900">
                <Key className="h-7 w-7" />
                <h2 className="text-2xl font-bold">Admin Invitation Codes</h2>
              </div>
              <p className="text-gray-800 text-sm mt-2">
                Generate temporary codes for new admin registrations (24-hour expiration)
              </p>
              {profile.admin_scope === 'department' && (
                <p className="text-gray-800 text-xs mt-2 bg-yellow-300 bg-opacity-40 px-3 py-2 rounded">
                  You can generate codes for <strong>{profile.department}</strong> admins only
                </p>
              )}
              {profile.admin_scope === 'super_admin' && (
                <div className="flex items-center gap-3 mt-3">
                  <select
                    value={codeType}
                    onChange={(e) => {
                      const val = e.target.value as 'department' | 'super_admin'
                      setCodeType(val)
                      if (val === 'super_admin') setCodeDepartment('Records')
                      else setCodeDepartment('')
                    }}
                    className="px-3 py-1.5 pr-8 text-sm rounded-lg border border-yellow-600 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-600"
                  >
                    <option value="department">Department Code</option>
                    <option value="super_admin">Super Admin Code</option>
                  </select>
                  {codeType === 'department' ? (
                    <select
                      value={codeDepartment}
                      onChange={(e) => setCodeDepartment(e.target.value)}
                      className="px-3 py-1.5 pr-8 text-sm rounded-lg border border-yellow-600 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-600"
                    >
                      <option value="">Select department</option>
                      {departments.map((dept) => (
                        <option key={dept.id} value={dept.name}>{dept.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value="Records"
                      disabled
                      className="px-3 py-1.5 text-sm rounded-lg border border-yellow-600 bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 mt-2">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  codesGeneratedToday >= 3 
                    ? 'bg-red-100 text-red-800' 
                    : codesGeneratedToday >= 2 
                    ? 'bg-amber-100 text-amber-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {3 - codesGeneratedToday} codes remaining today
                </span>
                {codesGeneratedToday >= 3 && (
                  <span className="text-xs text-gray-800">
                    (Resets at midnight)
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={generateAdminCode}
              disabled={generatingCode || codesGeneratedToday >= 3}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors shadow-lg ${
                generatingCode || codesGeneratedToday >= 3
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-blue-900 text-white hover:bg-blue-800'
              }`}
            >
              {generatingCode ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  Generating...
                </>
              ) : codesGeneratedToday >= 3 ? (
                <>
                  <XCircle className="h-5 w-5" />
                  Limit Reached
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5" />
                  Generate Code
                </>
              )}
            </button>
          </div>
        </div>

        <div className="p-8">
          {/* Codes List */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Generated Codes</h3>
            {adminCodes.length === 0 ? (
              <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-xl">
                <Key className="h-16 w-16 mx-auto mb-3 text-gray-400" />
                <p className="text-lg font-medium">No admin codes generated yet</p>
                <p className="text-sm mt-1">Click "Generate Code" to create a new invitation code</p>
              </div>
            ) : (
              <div className="space-y-3">
                {adminCodes.map((adminCode) => {
                  const expired = isCodeExpired(adminCode.expires_at)
                  const isUsed = adminCode.used_at !== null
                  const canUse = adminCode.is_active && !expired && !isUsed

                  return (
                    <div
                      key={adminCode.id}
                      className={`border-2 rounded-xl p-5 transition-all ${
                        canUse
                          ? 'border-green-200 bg-green-50'
                          : 'border-gray-200 bg-gray-50 opacity-75'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <code className="text-xl font-mono font-bold text-gray-900 bg-white px-4 py-2 rounded-lg border-2 border-gray-300">
                              {adminCode.code}
                            </code>
                            <button
                              onClick={() => copyToClipboard(adminCode.code)}
                              className="p-2 hover:bg-white rounded-lg transition-colors"
                              title="Copy to clipboard"
                            >
                              <Copy className="h-5 w-5 text-gray-600" />
                            </button>
                            {canUse ? (
                              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 text-sm font-semibold rounded-full">
                                <CheckCircle className="h-4 w-4" />
                                Active
                              </span>
                            ) : isUsed ? (
                              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
                                <CheckCircle className="h-4 w-4" />
                                Used
                              </span>
                            ) : expired ? (
                              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 text-gray-600 text-sm font-semibold rounded-full">
                                <XCircle className="h-4 w-4" />
                                Expired
                              </span>
                            ) : (
                              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 text-sm font-semibold rounded-full">
                                <XCircle className="h-4 w-4" />
                                Inactive
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-6 text-sm text-gray-600">
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                              adminCode.admin_type === 'super_admin' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {adminCode.admin_type === 'super_admin' ? 'Super Admin' : adminCode.for_department || 'Department'}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <Clock className="h-4 w-4" />
                              Created: {new Date(adminCode.created_at).toLocaleString()}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <Clock className="h-4 w-4" />
                              Expires: {new Date(adminCode.expires_at).toLocaleString()}
                            </span>
                          </div>
                        </div>
                        {canUse && (
                          <button
                            onClick={() => deactivateCode(adminCode.id)}
                            className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminProfilePage
